import React from 'react'
import { CircleDollarFilled } from '@/atoms/Icons/CircleDollarFilled'
import { CircleGiftFilled } from '@/atoms/Icons/CircleGiftFilled'
import { CircleTicketFilled } from '@/atoms/Icons/CircleTicketFilled'
import { LightIconFilled } from '@/atoms/Icons/LightIconFilled'
import { ShareIconFilled } from '@/atoms/Icons/ShareIconFilled'
import { AsH3, AsLabel, ParagraphMD, ParagraphSM, TitleLG, TitleSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { MissionExplainerCard } from '@/molecules/MissionExplainerCard'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface PayItForwardExplainerSectionProps {
  slug?: string
}

export const PayItForwardExplainerSection: React.FC<PayItForwardExplainerSectionProps> = ({ slug }) => {
  const { t } = useTranslate('pif')
  const showYoungDavid = slug === slugs.youngDavid
  const fundFuture = showYoungDavid
    ? t('helpCreateFutureEpisodes', 'Help Create Future Episodes and Seasons')
    : t('fundFutureSeasonsProjects', 'Fund Future Seasons and Projects')
  const fundFutureDescription = showYoungDavid
    ? t(
        'fundFutureDescription',
        'Every contribution helps directors, writers, animators, and so many other creators do what they do best without charging exorbitant theater prices or monthly fees.',
      )
    : t(
        'majorityPayItForwardContributionFilmmakers',
        'The majority of your Pay it Forward contribution goes to the filmmakers: the artists, writers, producers, directors, animators, and crews behind the scenes. This allows them to do what they do best and bring you more stories to enjoy.',
      )
  const makeContentFree = showYoungDavid
    ? t('makeContentFree', 'Make Content Free to Watch')
    : t('shareStoryWorld', 'Share this Story with the World')
  const makeContentFreeDescription = showYoungDavid
    ? t(
        'makeContentFreeDescription',
        'Not only does Pay it Forward benefit you, but it allows you to amplify light around the globe. You can know that your dollars are helping people enjoy wholesome, entertaining content.',
      )
    : t(
        'contributionHelpsAngelFundingMarketing',
        'Your contribution also helps Angel bring these stories to more people—by funding marketing and distribution, improving the Angel streaming experience on your TV, phone, and computer, and translating shows into new languages for global audiences.',
      )

  const gainSpecialPerks = showYoungDavid
    ? t('gainSpecialPerks', 'Gain Special Perks and Rewards')
    : t('helpAngelAmplifyLight', 'Help Angel Amplify Light')
  const gainSpecialPerksDescription = showYoungDavid
    ? t(
        'gainSpecialPerksDescription',
        'You can earn perks based on your level of contribution. Perks are decided by the individual creators. This could be a set visit, your name in the credits, or something special from your favorite show!',
      )
    : t(
        'payItForwardSharingStories',
        'At its heart, Pay it Forward is about sharing stories that matter. By supporting Angel, you become part of our mission to champion light-filled storytelling that inspires, uplifts, and transforms.',
      )
  return (
    <section id="what-is-pay-it-forward">
      <TitleLG className="mb-4" as={AsH3} color="core-gray-950" weight="bold">
        {t('whatIsPIF', 'What is Pay it Forward?')}
      </TitleLG>
      <ParagraphMD className="mb-8" color="core-gray-700">
        {showYoungDavid ? (
          <Translate t={t} i18nKey="whatIsPIFDescriptionv3">
            Angel Studios’ shows are free for viewers around the world, and we intend to keep it that way. But it takes
            millions of dollars to create and distribute high-quality content. That’s where you come in. Here are a few
            reasons why you play a critical role.
          </Translate>
        ) : (
          <Translate t={t} i18nKey="whatIsPIFDescriptionv4">
            Creating and distributing world-class, meaningful entertainment takes millions of dollars—and we can’t do it
            without you. Pay it Forward is your chance to directly support the shows you love and help bring more
            stories to life. Here’s how your contribution makes an impact:
          </Translate>
        )}
      </ParagraphMD>
      <div className="mb-16 grid grid-cols-1 gap-4 md:grid-cols-3">
        <MissionExplainerCard href="#fund-future-episodes" iconComponent={CircleDollarFilled} heading={fundFuture} />
        <MissionExplainerCard
          href="#make-content-free"
          iconComponent={showYoungDavid ? CircleTicketFilled : ShareIconFilled}
          heading={makeContentFree}
        />
        <MissionExplainerCard
          href="#gain-special-perks"
          iconComponent={showYoungDavid ? CircleGiftFilled : LightIconFilled}
          heading={gainSpecialPerks}
        />
      </div>
      <div>
        <section id="fund-future-episodes" className="mb-8">
          <TitleSM className="mb-2" weight="bold" as={AsLabel} color="core-gray-950">
            {fundFuture}
          </TitleSM>
          <ParagraphSM color="core-gray-700">{fundFutureDescription}</ParagraphSM>
        </section>
        <section id="make-content-free" className="mb-8">
          <TitleSM className="mb-2" weight="bold" as={AsLabel} color="core-gray-950">
            {makeContentFree}
          </TitleSM>
          <ParagraphSM color="core-gray-700">{makeContentFreeDescription}</ParagraphSM>
        </section>
        <section id="gain-special-perks" className="mb-8">
          <TitleSM className="mb-2" weight="bold" as={AsLabel} color="core-gray-950">
            {gainSpecialPerks}
          </TitleSM>
          <ParagraphSM color="core-gray-700">{gainSpecialPerksDescription}</ParagraphSM>
        </section>
      </div>
    </section>
  )
}
